import { useEffect, useState } from "react";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography as T,
  useTheme,
} from "@mui/material";
import ColorRadio from "datasets/components/ColorRadio";
import useDataset from "datasets/hooks/useDataset";
import useDatasets from "datasets/hooks/useDatasets";
import usePreviewMode from "datasets/hooks/usePreviewMode";
import DeleteDatasetModal from "datasets/pages/settings/components/DeleteDatasetModal";
import EnablePreviewModeModal from "datasets/pages/settings/components/EnablePreviewModeModal";
import AccentColor from "datasets/types/enums/accent-color";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import Dataset from "datasets/types/interfaces/dataset";
import { getAccentColor } from "datasets/utils/color";
import { format } from "date-fns";
import useErrorToast from "~/contexts/error-toast";
import useDatasetPermission from "~/pages/datasets/hooks/useDatasetPermission";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const formatDateTime = (timestamp: string) => {
  const date = new Date(timestamp);
  return format(date, "MM/dd/yyyy 'at' h:mma");
};

const CopyIconButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Failed to copy text:", e);
    }
  };

  return (
    <Tooltip title={copied ? "Copied" : "Copy to clipboard"}>
      <IconButton size="small" onClick={handleCopy} aria-label="Copy to clipboard">
        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};

const GeneralSettings = () => {
  const theme = useTheme();
  const { data: dataset, mutate } = useDataset();
  const { mutate: mutateDatasets } = useDatasets();
  const { showError } = useErrorToast();

  // Track confirmed color (what's actually saved) vs clicked color (visual feedback)
  const [confirmedColor, setConfirmedColor] = useState<AccentColor>(
    getAccentColor(dataset?.accentColor) || AccentColor.PURPLE
  );
  const [clickedColor, setClickedColor] = useState<AccentColor>(
    getAccentColor(dataset?.accentColor) || AccentColor.PURPLE
  );
  const [pendingColor, setPendingColor] = useState<AccentColor | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const canEdit = useDatasetPermission("edit:catalog-dataset");
  const canDelete = dataset?.actions_permitted.includes("delete:catalog-dataset") && canEdit;

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [previewModeModalOpen, setPreviewModeModalOpen] = useState(false);

  // Preview Mode: Feature flag controls section visibility, dataset.previewEnabled tracks state
  const isPreviewModeFeatureEnabled = usePreviewMode();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;
  const canEnablePreviewMode = isOwner && !dataset?.previewEnabled;

  useEffect(() => {
    if (!pendingColor || !dataset || pendingColor === confirmedColor) {
      return;
    }

    const controller = new AbortController();
    setIsSaving(true);

    // Debounce the API call to avoid multiple calls when user is selecting a color
    const timeoutId = setTimeout(async () => {
      try {
        const resp = await fetch(`${VITE_CATALOG_API}/datasets/${dataset.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            accentColor: pendingColor,
          }),
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!resp.ok) {
          throw Error("There was a problem trying to update the dataset");
        }

        const updatedDataset: Dataset = await resp.json();
        trackEvent("dataset_accent_color_updated", {
          dataset_id: dataset.id,
          accent_color: updatedDataset.accentColor,
        });
        mutate((currentDataset) => {
          if (!currentDataset || currentDataset.id !== dataset.id) {
            return currentDataset;
          }
          return { ...currentDataset, accentColor: updatedDataset.accentColor };
        });
        mutateDatasets();

        // Confirm the color change - now both states match
        setConfirmedColor(pendingColor);
        setPendingColor(null);
        setIsSaving(false);
      } catch (e) {
        setIsSaving(false);
        // On error, revert clicked color to confirmed color
        setClickedColor(confirmedColor);
        setPendingColor(null);

        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        console.error(e);
        rumClient?.recordError(e);
        showError("Unable to update dataset accent color");
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
      setIsSaving(false);
    };
  }, [dataset, mutate, mutateDatasets, pendingColor, confirmedColor, showError]);

  const handleColorClick = (color: AccentColor) => {
    if (color !== confirmedColor) {
      setClickedColor(color); // Immediate visual feedback
      setPendingColor(color); // Trigger API call
    }
  };

  return (
    <Stack>
      <T component="h1" fontSize={20} fontWeight={600} lineHeight={1.8}>
        General
      </T>
      <Stack gap={2} borderBottom={`1px solid ${theme.palette.grey[300]}`} py={2}>
        <T fontSize={14} fontWeight={600}>
          Accent Color
        </T>
        <Box sx={{ display: "flex", gap: 1 }}>
          {Object.values(AccentColor).map((option) => (
            <ColorRadio
              key={option}
              loading={isSaving && pendingColor === option}
              selected={clickedColor === option} // Show selected border immediately when clicked
              showCheck={confirmedColor === option} // Only show check when confirmed
              disabled={!canEdit || isSaving}
              value={option}
              onClick={handleColorClick}
            />
          ))}
        </Box>
      </Stack>

      {isPreviewModeFeatureEnabled && (
        <Stack gap={1} borderBottom={`1px solid ${theme.palette.grey[300]}`} py={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <T fontSize={14} fontWeight={600}>
              Preview Mode
            </T>
            {dataset?.previewEnabled && (
              <Chip label="Enabled" size="small" color="success" variant="outlined" />
            )}
          </Box>
          <T variant="body2" color="text.secondary">
            Allow this dataset to be shared with preview access, giving users visibility into
            metadata without exposing raw data.
          </T>
          {canEnablePreviewMode && (
            <Box pt={1}>
              <Button variant="outlined" onClick={() => setPreviewModeModalOpen(true)}>
                Enable Preview Mode
              </Button>
            </Box>
          )}
        </Stack>
      )}

      <Stack gap={1} borderBottom={`1px solid ${theme.palette.grey[300]}`} py={2}>
        <T fontSize={14} fontWeight={600}>
          Created
        </T>
        <T variant="body2">{dataset?.created_at && formatDateTime(dataset.created_at)}</T>
      </Stack>

      <Stack gap={1} borderBottom={`1px solid ${theme.palette.grey[300]}`} py={2}>
        <T fontSize={14} fontWeight={600}>
          Updated at
        </T>
        <T variant="body2">{dataset?.updated_at && formatDateTime(dataset.updated_at)}</T>
      </Stack>

      <Stack gap={1} borderBottom={`1px solid ${theme.palette.grey[300]}`} py={2}>
        <T fontSize={14} fontWeight={600}>
          ID
        </T>
        <Box display="flex" alignItems="center" gap={0.5}>
          <T variant="body2" sx={{ fontFamily: "monospace" }}>
            {dataset?.id}
          </T>
          <CopyIconButton text={String(dataset?.id)} />
        </Box>
      </Stack>

      {dataset?.slug && (
        <Stack gap={1} borderBottom={`1px solid ${theme.palette.grey[300]}`} py={2}>
          <T fontSize={14} fontWeight={600}>
            Slug
          </T>
          <Box display="flex" alignItems="center" gap={0.5}>
            <T variant="body2" sx={{ fontFamily: "monospace" }}>
              {dataset.slug}
            </T>
            <CopyIconButton text={dataset.slug} />
          </Box>
        </Stack>
      )}

      <Box pt={3}>
        {canDelete && (
          <Button
            variant="contained"
            color="error"
            startIcon={<ArchiveOutlinedIcon />}
            onClick={() => setDeleteModalOpen(true)}
          >
            Archive Dataset
          </Button>
        )}
      </Box>
      {dataset && (
        <>
          <DeleteDatasetModal
            id={dataset.id}
            name={dataset.title}
            open={deleteModalOpen}
            onClose={() => setDeleteModalOpen(false)}
          />
          {isPreviewModeFeatureEnabled && (
            <EnablePreviewModeModal
              id={dataset.id}
              name={dataset.title}
              open={previewModeModalOpen}
              onClose={() => setPreviewModeModalOpen(false)}
            />
          )}
        </>
      )}
    </Stack>
  );
};

export default GeneralSettings;
